import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FcFile, FcGallery } from 'react-icons/fc';
import { IoAlertCircleOutline } from 'react-icons/io5';
import { Card, Divider, Flex, Typography } from 'antd';
import { Asset, SideWithAsset } from 'shared-types';

import useAssetsStore from 'hooks/stores/useAssetsStore';
import useSessionStore from 'hooks/stores/useSessionStore';

import ResolutionMessage from './ResolutionMessage';
import QualityChecklist from './WarningMessages';
import PageDetailsModal from './PageDetailsModal';

type Props = {
  side: SideWithAsset;
  totalPageCount: number;
  sideNumber: number;
};

export default function ResultOutput({ side, totalPageCount, sideNumber }: Props) {
  const session = useSessionStore((state) => state.session);
  const asset = useAssetsStore((state) => state.byId[side?.assetId]);
  const { t } = useTranslation();
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

  if (!session || !side || !asset) return null;

  // Get UI rightBar configuration from session options (master toggle)
  const rightBar = session?.options?.ui?.rightBar;
  const shouldShowRightBar = rightBar?.enabled !== false;

  // Get UI rightBar tipSection configuration from session options
  const tipSection = session?.options?.ui?.rightBar?.tipSection;
  const shouldShowTipSection = tipSection?.enabled !== false;
  const tipTitle = tipSection?.title || t('ResultOutput.Tip');
  const tipText = tipSection?.text || t('ResultOutput.MouseZoomTip');

  // Get UI rightBar sourceFileSection configuration from session options
  const sourceFileSection = session?.options?.ui?.rightBar?.sourceFileSection;
  const shouldShowSourceFileSection = sourceFileSection?.enabled !== false;
  const shouldShowFilename = sourceFileSection?.filename !== false;
  const shouldShowSourcePageNumber = sourceFileSection?.pageNumber !== false;
  const shouldShowSize = sourceFileSection?.size !== false;

  // Get UI rightBar yourProductSection configuration from session options
  const yourProductSection = session?.options?.ui?.rightBar?.yourProductSection;
  const shouldShowYourProductSection = yourProductSection?.enabled !== false;
  const shouldShowPagesCount = yourProductSection?.pagesCount !== false;
  const shouldShowProductSize = yourProductSection?.productSize !== false;
  const shouldShowMinimalResolution = yourProductSection?.minimalResolution !== false;

  // Get UI rightBar pageInfoSection configuration from session options
  const pageInfoSection = session?.options?.ui?.rightBar?.pageInfoSection;
  const shouldShowPageInfoSection = pageInfoSection?.enabled !== false;
  const shouldShowPageInfoPageNumber = pageInfoSection?.pageNumber !== false;
  const shouldShowScaling = pageInfoSection?.scaling !== false;
  const shouldShowFileFormat = pageInfoSection?.fileFormat !== false;
  const shouldShowFont = pageInfoSection?.font !== false;
  const shouldShowResolution = pageInfoSection?.resolution !== false;
  const shouldShowColorCount = pageInfoSection?.colorCount !== false;
  const shouldShowDetailsLink = pageInfoSection?.showDetailsLink !== false;

  // Get UI rightBar qualityControlSection configuration from session options
  const qualityControlSection = session?.options?.ui?.rightBar?.qualityControlSection;
  const shouldShowQualityControlSection = qualityControlSection?.enabled !== false;

  const {
    sidesCount,
    productSize,
    options: { minimalDpi },
  } = session;
  const { resizeMetadata } = side;
  const { filename, pageNumber, metadata } = asset;
  const { scalingPercentage, lowestResolution } = resizeMetadata;

  const isScalingOutOfRange = scalingPercentage < 99.9 || scalingPercentage > 100.1;

  // Calculate aspect ratios to determine if scaling is proportional
  const productAspectRatio = Number(productSize.width) / Number(productSize.height);
  const sourceAspectRatio =
    metadata.page?.widthMM && metadata.page?.heightMM
      ? Number(metadata.page.widthMM) / Number(metadata.page.heightMM)
      : null;

  // Check if aspect ratios are significantly different (allowing 5% tolerance)
  const isAspectRatioDistorted = sourceAspectRatio
    ? Math.abs(productAspectRatio - sourceAspectRatio) / productAspectRatio > 0.05
    : false;

  return shouldShowRightBar ? (
    <Card>
      {/* 1. Kwaliteitscontrole */}
      {shouldShowQualityControlSection && (
        <QualityChecklist
          actualSidesCount={totalPageCount}
          sessionsSidesCount={sidesCount}
          lowestResolution={lowestResolution}
          minimalDpi={minimalDpi}
          scalingPercentage={scalingPercentage}
          isAspectRatioDistorted={isAspectRatioDistorted}
          hasOverprint={metadata.hasOverprint}
          colorCount={
            side.qualityCheck?.colorCount
              ? {
                  actualCount: side.qualityCheck.colorCount.actualCount,
                  status: side.qualityCheck.colorCount.status,
                  isValid: side.qualityCheck.colorCount.isValid,
                  requirement: side.qualityCheck.colorCount.requirement,
                }
              : undefined
          }
        />
      )}

      {/* 2. Pagina Info */}
      {shouldShowPageInfoSection && (
        <>
          <Divider>
            <Flex justify="space-between" align="center">
              <FcFile style={{ fontSize: '1.5rem' }} />
              <Typography.Text strong>{t('ResultOutput.PageInfo')}</Typography.Text>
            </Flex>
          </Divider>

          <Flex vertical justify="flex-start" align="flex-start">
            {shouldShowPageInfoPageNumber && (
              <Flex justify="flex-start" align="center">
                <Typography.Text style={{ fontSize: '0.9rem', minWidth: '140px' }}>
                  {t('ResultOutput.PageNumber')}:
                </Typography.Text>
                <Typography.Text style={{ fontSize: '0.9rem' }}>
                  <strong>{sideNumber}</strong>
                </Typography.Text>
              </Flex>
            )}
            {shouldShowScaling && (
              <Flex justify="flex-start" align="center">
                <Typography.Text style={{ fontSize: '0.9rem', minWidth: '140px' }}>
                  {t('ResultOutput.Scaling')}:
                </Typography.Text>
                {isScalingOutOfRange ? (
                  <Flex
                    justify="flex-start"
                    align="center"
                    title={
                      isAspectRatioDistorted
                        ? t('ResultOutput.ScalingDistorted') || ''
                        : t('ResultOutput.ScalingProportional') || ''
                    }
                    style={{
                      backgroundColor: isAspectRatioDistorted ? '#fff2f0' : '#fffbe6',
                      borderRadius: '6px',
                      padding: '2px 6px',
                      border: isAspectRatioDistorted ? '1px solid #ffccc7' : '1px solid #ffe58f',
                      cursor: 'help',
                    }}
                  >
                    <Typography.Text
                      style={{
                        fontSize: '0.9rem',
                        color: isAspectRatioDistorted ? '#cf1322' : '#faad14',
                        fontWeight: 'bold',
                      }}
                    >
                      {scalingPercentage}%
                    </Typography.Text>
                    <IoAlertCircleOutline
                      style={{
                        fontSize: '0.9rem',
                        marginLeft: '0.3rem',
                        color: isAspectRatioDistorted ? '#cf1322' : '#faad14',
                      }}
                    />
                  </Flex>
                ) : (
                  <Typography.Text style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
                    {scalingPercentage}%
                  </Typography.Text>
                )}
              </Flex>
            )}
            {shouldShowFileFormat && (
              <Flex justify="flex-start" align="center">
                <Typography.Text style={{ fontSize: '0.9rem', minWidth: '140px' }}>
                  {t('ResultOutput.FileFormat')}:
                </Typography.Text>
                <Typography.Text style={{ fontSize: '0.9rem' }}>
                  <strong>PDF</strong>
                </Typography.Text>
              </Flex>
            )}
            {shouldShowFont && (
              <Flex justify="flex-start" align="center">
                <Typography.Text style={{ fontSize: '0.9rem', minWidth: '140px' }}>
                  {t('ResultOutput.FontCount')}:
                </Typography.Text>
                <Typography.Text style={{ fontSize: '0.9rem' }}>
                  <strong>{metadata.fonts?.length || 0}</strong>
                </Typography.Text>
              </Flex>
            )}
            {shouldShowColorCount && metadata.colors && (() => {
              // Format requirement text with translation
              const getRequirementText = () => {
                if (!side.qualityCheck?.colorCount?.requirement) return '';
                const { type, value } = side.qualityCheck.colorCount.requirement;
                if (type === 'max') {
                  return `${t('QualityCheck.Max')} ${value}`;
                } else if (type === 'min') {
                  return `${t('QualityCheck.Min')} ${value}`;
                } else {
                  return `${t('QualityCheck.Exactly')} ${value}`;
                }
              };

              const requirementText = getRequirementText();

              return (
                <Flex justify="flex-start" align="center">
                  <Typography.Text style={{ fontSize: '0.9rem', minWidth: '140px' }}>
                    {t('ResultOutput.ColorCount')}:
                  </Typography.Text>
                  {side.qualityCheck?.colorCount &&
                  !side.qualityCheck.colorCount.isValid ? (
                    <Flex
                      justify="flex-start"
                      align="center"
                      title={
                        requirementText
                          ? side.qualityCheck.colorCount.status === 'error'
                            ? (t('ResultOutput.ColorCountRequiredHint', {
                                requirement: requirementText,
                              }) as string)
                            : (t('ResultOutput.ColorCountExpectedHint', {
                                requirement: requirementText,
                              }) as string)
                          : undefined
                      }
                      style={{
                        backgroundColor:
                          side.qualityCheck.colorCount.status === 'error' ? '#fff2f0' : '#fffbe6',
                        borderRadius: '6px',
                        padding: '2px 6px',
                        border:
                          side.qualityCheck.colorCount.status === 'error'
                            ? '1px solid #ffccc7'
                            : '1px solid #ffe58f',
                        cursor: requirementText ? 'help' : 'default',
                      }}
                    >
                      <Typography.Text
                        style={{
                          fontSize: '0.9rem',
                          color: side.qualityCheck.colorCount.status === 'error' ? '#cf1322' : '#faad14',
                          fontWeight: 'bold',
                        }}
                      >
                        {metadata.colors.length}
                      </Typography.Text>
                      <IoAlertCircleOutline
                        style={{
                          fontSize: '0.9rem',
                          marginLeft: '0.3rem',
                          color: side.qualityCheck.colorCount.status === 'error' ? '#cf1322' : '#faad14',
                        }}
                      />
                    </Flex>
                  ) : (
                    <Typography.Text
                      style={{ fontSize: '0.9rem', fontWeight: 'bold' }}
                      title={
                        requirementText
                          ? (t('ResultOutput.ColorCountCorrectHint', {
                              requirement: requirementText,
                            }) as string)
                          : undefined
                      }
                    >
                      {metadata.colors.length}
                    </Typography.Text>
                  )}
                </Flex>
              );
            })()}
            {shouldShowResolution && (
              <ResolutionMessage lowestResolution={lowestResolution} minimalDpi={minimalDpi} />
            )}
            {shouldShowDetailsLink && (
              <Flex justify="flex-start" align="center" style={{ marginTop: '8px' }}>
                <Typography.Link
                  onClick={() => setIsDetailsModalOpen(true)}
                  style={{ fontSize: '0.9rem' }}
                >
                  {t('ResultOutput.ViewDetails')}
                </Typography.Link>
              </Flex>
            )}
          </Flex>
        </>
      )}

      {/* 3. Jouw Product */}
      {shouldShowYourProductSection && (
        <>
          <Divider>
            <Flex justify="space-between" align="center">
              <FcGallery style={{ fontSize: '1.5rem' }} />
              <Typography.Text strong>{t('ResultOutput.YourProduct')}</Typography.Text>
            </Flex>
          </Divider>
          <Flex vertical justify="flex-start" align="flex-start">
            {shouldShowPagesCount && (
              <Flex justify="flex-start" align="center">
                <Typography.Text style={{ minWidth: '140px' }}>
                  {t('ResultOutput.PagesCount')}:
                </Typography.Text>
                <Typography.Text>
                  <strong>{sidesCount}</strong>
                </Typography.Text>
              </Flex>
            )}
            {shouldShowProductSize && (
              <Flex justify="flex-start" align="center">
                <Typography.Text style={{ minWidth: '140px' }}>
                  {t('ResultOutput.ProductSize')}:
                </Typography.Text>
                <Typography.Text>
                  <strong>
                    {productSize.width} x {productSize.height}
                  </strong>
                </Typography.Text>
              </Flex>
            )}
            {shouldShowMinimalResolution && (
              <Flex justify="flex-start" align="center">
                <Typography.Text style={{ minWidth: '140px' }}>
                  {t('ResultOutput.MinimalResolution')}:
                </Typography.Text>
                <Typography.Text>
                  <strong>{minimalDpi} DPI</strong>
                </Typography.Text>
              </Flex>
            )}
          </Flex>
        </>
      )}

      {/* 4. Bronbestand */}
      {shouldShowSourceFileSection && (
        <>
          <Divider>
            <Flex justify="space-between" align="center">
              <Typography.Text strong>{t('ResultOutput.SourceFile')}</Typography.Text>
            </Flex>
          </Divider>

          <Flex vertical justify="flex-start" align="flex-start">
            {shouldShowFilename && (
              <Flex justify="flex-start" align="center">
                <Typography.Text style={{ fontSize: '0.9rem', minWidth: '140px' }}>
                  {t('ResultOutput.Filename')}:
                </Typography.Text>
                <Typography.Text style={{ fontSize: '0.9rem' }}>
                  <strong>{filename}</strong>
                </Typography.Text>
              </Flex>
            )}
            {shouldShowSourcePageNumber && (
              <Flex justify="flex-start" align="center">
                <Typography.Text style={{ fontSize: '0.9rem', minWidth: '140px' }}>
                  {t('ResultOutput.PageNumber')}:
                </Typography.Text>
                <Typography.Text style={{ fontSize: '0.9rem' }}>
                  <strong>{pageNumber}</strong>
                </Typography.Text>
              </Flex>
            )}
            {shouldShowSize && (
              <Flex justify="flex-start" align="center">
                <Typography.Text style={{ fontSize: '0.9rem', minWidth: '140px' }}>
                  {t('ResultOutput.Size')}:
                </Typography.Text>
                <Typography.Text style={{ fontSize: '0.9rem' }}>
                  <strong>
                    {metadata.page?.widthMM} * {metadata.page?.heightMM} mm
                  </strong>
                </Typography.Text>
              </Flex>
            )}
          </Flex>
        </>
      )}

      {/* 5. Tip (blijft onderaan) */}
      {shouldShowTipSection && (
        <>
          <Divider>
            <Flex justify="space-between" align="center">
              <Typography.Text strong>{tipTitle}</Typography.Text>
            </Flex>
          </Divider>
          <Flex vertical justify="flex-start" align="flex-start">
            <Typography.Text strong style={{ fontSize: '0.9rem' }}>
              {tipText}
            </Typography.Text>
          </Flex>
        </>
      )}

      {/* Page Details Modal */}
      <PageDetailsModal
        isOpen={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
        side={side}
      />
    </Card>
  ) : null;
}
